<template>
    <DrawerRoot :open="open" :modal="modal === true ? true : 'trap-focus'" :swipe-direction="swipeDirection" @update:open="handleOpenChange">
        <SwipeLock :locked="!swipeToClose" />
        <!-- Only rendered when a trigger is supplied: an empty DrawerTrigger registers an arbitrary
             element as the return-focus target (see BaseDialog / LAB-1559). Adopters are expected to
             control the drawer through v-model:open. -->
        <DrawerTrigger v-if="$slots.trigger" as-child>
            <slot name="trigger"></slot>
        </DrawerTrigger>
        <DrawerPortal :disabled="disablePortal">
            <!-- Not rendered at all when the page stays usable: reka's overlay also locks body scroll. -->
            <DrawerOverlay v-if="isPageBlocked" :class="[classes.overlay, overlayClass]" />
            <DrawerContent
                v-bind="description ? {} : { 'aria-describedby': undefined }"
                :final-focus="finalFocusTarget"
                :class="[
                    classes.content,
                    side === 'right' ? classes.right : classes.bottom,
                    mobileLayout === 'fullscreen' && classes.fullscreen,
                    reserveMobileNavSpace ? classes.mobileNavInset : classes.safeAreaInset,
                    drawerContentClass,
                ]"
                @open-auto-focus="captureOpener"
                @interact-outside="handleInteractOutside"
                @escape-key-down="handleEscapeKeyDown">
                <DrawerHandle v-if="swipeToClose && swipeDirection === 'down'" :class="classes.handle" />
                <VisuallyHidden as-child>
                    <DrawerDescription v-if="description">{{ description }}</DrawerDescription>
                </VisuallyHidden>
                <div :class="classes.header">
                    <!-- The accessible name and the visible heading are deliberately the same string:
                         every hand-rolled panel this replaces was announced as an unnamed "dialog". -->
                    <DrawerTitle :class="[classes.heading, titleVoiceClasses[titleVoice], !wrapTitle && 'truncate']">{{ title }}</DrawerTitle>
                    <DrawerClose
                        v-if="showClose"
                        ref="closeControl"
                        :disabled="!dismissible"
                        :aria-label="t('components.drawer.close')"
                        class="p-2 -m-2 bi bi-x-lg text-base/none icon-semibold"
                        :class="classes.close"></DrawerClose>
                </div>
                <div :class="classes.body">
                    <slot></slot>
                </div>
                <slot name="footer"></slot>
            </DrawerContent>
        </DrawerPortal>
    </DrawerRoot>
</template>

<script setup lang="ts">
import { useMediaQuery } from "@vueuse/core";
import {
    DrawerClose,
    DrawerContent,
    DrawerDescription,
    DrawerHandle,
    DrawerOverlay,
    DrawerPortal,
    DrawerRoot,
    DrawerTitle,
    DrawerTrigger,
    injectDrawerRootContext,
    VisuallyHidden,
} from "reka-ui";
import type { ComponentPublicInstance } from "vue";
import { computed, defineComponent, shallowRef, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

// Controlled through v-model:open. Owning the binding (rather than letting it fall through to
// DrawerRoot) is what lets `dismissible` and `swipeToClose` refuse a close.
const open = defineModel<boolean>("open", { default: false });

interface Props {
    // The drawer's visible heading and accessible name. Required so an unnamed drawer is impossible.
    title: string;
    // "app" is the bold app heading, "serif" the reading-view heading, "plain" the regular-weight
    // body heading for panels whose title is content (a goal's own title) rather than a label.
    titleVoice?: "app" | "serif" | "plain";
    // Let a long title wrap onto more lines instead of truncating it to one.
    wrapTitle?: boolean;
    description?: string;
    // Anchored edge on desktop. Both sides render as a bottom sheet on mobile; "bottom" stays a
    // bottom sheet at every viewport (the MobileMoreSheet shape).
    side?: "right" | "bottom";
    // Full-screen reading view on mobile; the desktop side remains controlled by `side`.
    mobileLayout?: "sheet" | "fullscreen";
    showClose?: boolean;
    // `true` blocks the page under a backdrop at every width. `"mobile"` does that below md only:
    // from md up there is no backdrop and the page stays clickable (picking another item in a
    // list behind the panel), while focus still stays in the panel and Escape still closes it.
    // `false` drops the backdrop at every width. Outside clicks never close a non-blocking panel.
    modal?: boolean | "mobile";
    // Drag toward the anchored edge to close, with the sheet handle on mobile. Turn it off for
    // panels that hold a form or long content, where a stray drag should not dismiss them. Off, the
    // drawer does not track drags at all, so text in the panel selects and scrolls normally.
    swipeToClose?: boolean;
    // While false, nothing closes the drawer: the close button is disabled and Escape, outside
    // clicks and swipes are ignored. For holding the panel open while a save is in flight.
    dismissible?: boolean;
    disablePortal?: boolean;
    disableCloseOnBackdropClick?: boolean;
    reserveMobileNavSpace?: boolean;
    // Escape hatch for the portaled panel, which a consumer's class="" cannot reach (e.g. a custom width).
    drawerContentClass?: string;
    overlayClass?: string;
    onInteractOutside?: () => void | null;
    // Where focus should land when the drawer closes. Adopters open from route or nav changes where
    // nothing relevant holds focus, so the element focused at open time has nothing to restore.
    returnFocusTo?: HTMLElement | ComponentPublicInstance | null;
}

const props = withDefaults(defineProps<Props>(), {
    description: "",
    titleVoice: "app",
    wrapTitle: false,
    modal: true,
    swipeToClose: true,
    dismissible: true,
    side: "right",
    mobileLayout: "sheet",
    showClose: true,
    disablePortal: false,
    disableCloseOnBackdropClick: false,
    reserveMobileNavSpace: true,
    drawerContentClass: "",
    overlayClass: "",
    returnFocusTo: null,
});

const titleVoiceClasses: Record<NonNullable<Props["titleVoice"]>, string> = {
    app: "body-bold",
    serif: "title-card-serif",
    plain: "body",
};

// reka's Drawer has no switch for drag tracking: DrawerContent captures every mouse drag and calls
// preventDefault on touchmove, which blocks text selection and scrolling inside the panel. Its one
// gate is "a nested drawer is mid-swipe", read from the root context on each pointerdown and
// touchstart. With swipeToClose off, report that gate as always closed so a drag is never tracked.
const SwipeLock = defineComponent({
    props: { locked: { type: Boolean, required: true } },
    setup(lockProps) {
        const context = injectDrawerRootContext();
        const nestedSwiping = context.nestedSwiping;
        context.nestedSwiping = computed({
            get: () => lockProps.locked || nestedSwiping.value,
            set: (value) => {
                nestedSwiping.value = value;
            },
        });
        return () => null;
    },
});

// Full-screen reading panels keep the rightward swipe on mobile, where there is no sheet handle.
const isDesktop = useMediaQuery("(min-width: 768px)");
const swipeDirection = computed(() => (props.side === "right" && (isDesktop.value || props.mobileLayout === "fullscreen") ? "right" : "down"));

// Unlike DialogContent, DrawerContent does not remember the element focused at open time: with no
// DrawerTrigger its default final focus is nothing. Capture the opener so controlled drawers still
// return focus.
const opener = shallowRef<HTMLElement | null>(null);
const captureOpener = () => {
    const active = globalThis.document.activeElement;
    opener.value = active instanceof HTMLElement && active !== globalThis.document.body ? active : null;
};

const finalFocusTarget = computed(() => {
    const target = props.returnFocusTo instanceof HTMLElement ? props.returnFocusTo : props.returnFocusTo?.$el;
    return target instanceof HTMLElement ? target : (opener.value ?? true);
});

// The header's close button, for an adopter that needs a focus target inside the drawer (a nested
// dialog that removed its own opener). Null while the drawer is closed or showClose is off.
const closeControl = useTemplateRef<ComponentPublicInstance>("closeControl");
const closeButton = computed(() => {
    const el: unknown = closeControl.value?.$el;
    return el instanceof HTMLElement ? el : null;
});

defineExpose({ closeButton });

// Whether the page behind is under a backdrop right now; only then does an outside click close.
const isPageBlocked = computed(() => props.modal === true || (props.modal === "mobile" && !isDesktop.value));

function handleOpenChange(value: boolean, details?: { reason?: string }) {
    if (!value && (!props.dismissible || (!props.swipeToClose && details?.reason === "swipe"))) {
        return;
    }
    open.value = value;
}

const handleEscapeKeyDown = (event: Event) => {
    if (!props.dismissible) {
        event.preventDefault();
        return;
    }
    handleDismissRequest(event);
};

const handleInteractOutside = (event: Event) => {
    if (!isPageBlocked.value) {
        event.preventDefault();
        return;
    }
    handleDismissRequest(event);
};

const handleDismissRequest = (event: Event) => {
    if (!props.dismissible || props.disableCloseOnBackdropClick) {
        event.preventDefault();
        return;
    }
    // Prevent closing when the interaction targets a teleported element opted out of dismissal
    const originalTarget = (event as CustomEvent)?.detail?.originalEvent?.target as Element | null;
    if (originalTarget?.closest("[data-dismiss-ignore]")) {
        event.preventDefault();
        return;
    }
    if (props.onInteractOutside) {
        props.onInteractOutside();
    }
};
</script>

<style module="classes">
@reference "../../../css/tailwind.css";

@layer components {
    /* Colours come from css/tokens/dialog.css: a drawer is dialog-family chrome, so the tonal
       flag restyles both surfaces together. */
    .overlay {
        @apply bg-dialog-overlay/40 w-dvw h-dvh fixed top-0 left-0 z-30;
    }

    /* reka's Drawer tracks the drag but leaves rendering to CSS: it writes the pointer offset to
       --drawer-swipe-movement-x/y and sets data-swiping while a finger or pointer is down. The offset
       goes on the independent translate property, which composes with the enter/exit keyframes on
       transform, so a drag that starts during the entry slide still follows the finger.
       transition-transform covers translate too, easing a cancelled swipe back to the edge. */
    .content {
        @apply fixed z-30 flex flex-col bg-dialog-surface shadow-2xl focus-visible:outline-0 transition-transform duration-300 data-swiping:duration-0;
        translate: var(--drawer-anchored-x, 0px) var(--drawer-anchored-y, 0px);
    }

    /* reka also tracks drags away from the dismiss edge, so a wrong-way swipe would lift the sheet
       off the viewport edge and show backdrop behind it. Clamp movement to the dismiss direction. */
    .content[data-swipe-direction="down"] {
        --drawer-anchored-y: max(0px, var(--drawer-swipe-movement-y, 0px));
    }
    .content[data-swipe-direction="right"] {
        --drawer-anchored-x: max(0px, var(--drawer-swipe-movement-x, 0px));
    }

    /* Bottom sheet on mobile; full-height right panel from md up. */
    .right {
        @apply inset-x-0 bottom-0 max-h-[90dvh] rounded-t-2xl border-t border-border md:inset-y-0 md:right-0 md:left-auto md:w-[27.5rem] md:max-h-none md:rounded-none md:border-t-0 md:border-l;
    }

    .bottom {
        @apply inset-x-0 bottom-0 max-h-[90dvh] rounded-t-2xl border-t border-border;
    }

    .fullscreen {
        @apply max-md:inset-0 max-md:h-dvh max-md:max-h-none max-md:rounded-none max-md:border-0 max-md:pt-[env(safe-area-inset-top,0px)];
    }

    .handle {
        @apply absolute top-2 left-1/2 -translate-x-1/2 h-1 w-10 rounded-full bg-border-strong;
    }

    .header {
        @apply flex items-center justify-between gap-3 px-6 py-5 border-b border-border shrink-0;
    }

    .heading {
        @apply text-ink min-w-0 flex-1;
    }

    .close {
        @apply shrink-0 text-ink-muted hover:text-ink transition-colors;
    }

    .body {
        @apply grow overflow-y-auto;
    }

    .mobileNavInset {
        @apply pb-(--mobile-nav-total-height) md:pb-0;
    }

    .safeAreaInset {
        @apply pb-[env(safe-area-inset-bottom)] md:pb-0;
    }
}

/* Enter/exit keyed off reka's data-state and data-swipe-direction, so the slide axis always matches
   the swipe axis. reka's Presence keeps the node mounted until the exit animation finishes. On a
   swipe dismiss reka leaves the movement vars at their release value, so translate holds the
   drawer where it was let go and the exit keyframe only covers the remaining distance on top of
   it; --drawer-swipe-strength (set by reka on release) shortens the exit after a fast fling. */
.overlay[data-state="open"] {
    animation: fade-in 200ms ease;
}
.overlay[data-state="closed"] {
    animation: fade-out 200ms ease forwards;
}
.content[data-state="open"] {
    animation: slide-up 300ms ease;
}
.content[data-state="closed"] {
    animation: slide-down calc(300ms * var(--drawer-swipe-strength, 1)) ease forwards;
}
.content[data-swipe-direction="right"][data-state="open"] {
    animation-name: slide-in-right;
}
.content[data-swipe-direction="right"][data-state="closed"] {
    animation-name: slide-out-right;
}
@media (prefers-reduced-motion: reduce) {
    .overlay[data-state],
    .content[data-state],
    .content[data-swipe-direction][data-state] {
        animation: none;
        transition: none;
    }
}

@keyframes fade-in {
    from {
        opacity: 0;
    }
}
@keyframes fade-out {
    to {
        opacity: 0;
    }
}
@keyframes slide-up {
    from {
        transform: translateY(100%);
    }
}
@keyframes slide-down {
    to {
        transform: translateY(calc(100% - var(--drawer-anchored-y, 0px)));
    }
}
@keyframes slide-in-right {
    from {
        transform: translateX(100%);
    }
}
@keyframes slide-out-right {
    to {
        transform: translateX(calc(100% - var(--drawer-anchored-x, 0px)));
    }
}
</style>
